/**
 * Unified revenue analytics API (B3).
 *
 * One endpoint, one response, one snapshot. The backend composes B2 (expected),
 * A1 (service-period attribution) and B1 (aging + cashflow) inside a single
 * REPEATABLE READ transaction, so every block of the page refers to one state of
 * the world. Splitting this into per-tab requests would give that up for nothing.
 *
 * NOT the legacy revenue endpoints. `/api/admin/revenue-forecast` iterates rooms
 * and can non-deterministically drop one of two tenancies in a handover month,
 * and `/api/admin/revenue/monthly-forecast` folds one-time revenue into a figure
 * its own docstring calls "recurring". Neither is imported here, and the page
 * that consumes this helper must not reach for them either.
 *
 * Uses the same `API_BASE_URL` + `getApiHeaders` + `parseAdminErrorResponse`
 * path as every other admin client module.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

/**
 * GET /api/admin/revenue-analytics
 *
 * THE COMPARISON PAIR IS ALL-OR-NOTHING, AND THAT IS ENFORCED HERE.
 * The backend answers 400 when exactly one of `comparison_year` /
 * `comparison_month` arrives, because completing the other from the selected
 * month would silently compare against a period nobody asked for. Rather than
 * relying on every call site to remember that, this helper sends BOTH or
 * NEITHER — a half state cannot leave this function.
 *
 * Omitting both is the normal case: B2 resolves the preceding month itself and
 * reports it back as `comparison_period`, which is the only trustworthy source
 * for the "vs. <month>" labels. Never re-derive that client-side.
 *
 * THE WINDOW IS OPTIONAL AND BACKWARD COMPATIBLE (P3a).
 * `to_year` / `to_month` extend the selection to an inclusive month window.
 * Omitted — which is what every pre-P3b caller does — the request is byte-for-byte
 * the single-month one it has always been.
 *
 * They are named in WIRE form, unlike `comparisonYear` and `trendMonths`, because
 * they are the siblings of `year` and `month`: this helper already takes those two
 * verbatim, and `toYear` beside a plain `year` would be the odd one out rather than
 * the consistent one.
 *
 * Both or neither, for the same reason as the comparison pair: the backend answers
 * 400 to a half-given window, and completing it here from the selected month would
 * answer a period nobody asked for.
 *
 * @param {{
 *   year?: number, month?: number,
 *   to_year?: number|null, to_month?: number|null,
 *   comparisonYear?: number|null, comparisonMonth?: number|null,
 *   trendMonths?: number, forecastMonths?: number,
 *   signal?: AbortSignal
 * }} [params]
 * @returns {Promise<object>} RevenueAnalyticsResponse
 */
export function fetchRevenueAnalytics(params = {}) {
  const {
    year,
    month,
    to_year: toYear,
    to_month: toMonth,
    comparisonYear,
    comparisonMonth,
    trendMonths,
    forecastMonths,
    signal,
  } = params;

  const sp = new URLSearchParams();
  if (year != null) sp.set("year", String(year));
  if (month != null) sp.set("month", String(month));

  // Both or neither — see the note above.
  if (toYear != null && toMonth != null) {
    sp.set("to_year", String(toYear));
    sp.set("to_month", String(toMonth));
  }

  // Both or neither — see the note above.
  if (comparisonYear != null && comparisonMonth != null) {
    sp.set("comparison_year", String(comparisonYear));
    sp.set("comparison_month", String(comparisonMonth));
  }

  if (trendMonths != null) sp.set("trend_months", String(trendMonths));
  if (forecastMonths != null) sp.set("forecast_months", String(forecastMonths));

  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/revenue-analytics${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders(), signal }
  ).then(async (res) => {
    if (!res.ok) {
      const err = new Error(await parseAdminErrorResponse(res));
      // Carried so the page can turn 403 into a role explanation instead of
      // repeating a bare backend string the reader cannot act on.
      err.status = res.status;
      throw err;
    }
    return res.json();
  });
}
